/**
 * @typedef {10|15|20|30|60} LastPositionsSinceMinutes
 */

/**
 * @typedef {Object} LastPositionsParams
 * @property {number} [user_id]
 * @property {LastPositionsSinceMinutes} [since_minutes] - Live mode window (minutes)
 * @property {number} [history_days] - History mode window (days)
 * @property {boolean} [history] - true = full GPS trail; false/absent = latest per user+IP
 */

/**
 * @typedef {Object} UserLastActivityPositionUser
 * @property {number} id
 * @property {string|null} name
 * @property {string|null} phone
 */

/**
 * GPS position from activity logs (latest-per-user+IP or full history row).
 * @typedef {Object} UserLastActivityPosition
 * @property {number} user_id
 * @property {string} ip_address
 * @property {number} latitude
 * @property {number} longitude
 * @property {string} last_action
 * @property {string|null} description
 * @property {string} action_at
 * @property {number} activity_log_id
 * @property {UserLastActivityPositionUser} [user]
 */

/**
 * @typedef {Object} UserLastActivityPositionsMeta
 * @property {number} total - Row count (all GPS rows in history mode; latest positions in default mode)
 * @property {number|null} [since_minutes] - Present in live mode
 * @property {number|null} [history_days] - Present in history mode
 * @property {string|null} filtered_from
 * @property {boolean} history - true = full trail; false = latest per user+IP
 */

/**
 * @typedef {Object} LastPositionsResponse
 * @property {'success'} status
 * @property {string} message
 * @property {UserLastActivityPosition[]} data
 * @property {UserLastActivityPositionsMeta} meta
 */

/**
 * List/table key for a position row.
 * @param {UserLastActivityPosition} position
 * @param {boolean} [history=false]
 * @returns {string}
 */
export function getUserLastActivityPositionKey(position, history = false) {
  if (history) {
    return String(position.activity_log_id)
  }
  return `${position.user_id}-${position.ip_address}`
}

/**
 * Unique user count from a positions array.
 * @param {UserLastActivityPosition[]} positions
 * @returns {number}
 */
export function countUniqueUsersFromPositions(positions) {
  return new Set(positions.map(p => p.user_id)).size
}

/**
 * Sort positions by action_at ascending (oldest first) for map trails.
 * @param {UserLastActivityPosition[]} positions
 * @returns {UserLastActivityPosition[]}
 */
export function sortPositionsByActionAtAsc(positions) {
  return [...positions].sort(
    (a, b) => new Date(a.action_at).getTime() - new Date(b.action_at).getTime(),
  )
}

/**
 * Sort positions by action_at descending (newest first) for lists.
 * @param {UserLastActivityPosition[]} positions
 * @returns {UserLastActivityPosition[]}
 */
export function sortPositionsByActionAtDesc(positions) {
  return [...positions].sort(
    (a, b) => new Date(b.action_at).getTime() - new Date(a.action_at).getTime(),
  )
}

/**
 * Group positions by user_id + ip_address for per-device trails.
 * @param {UserLastActivityPosition[]} positions
 * @returns {Record<string, UserLastActivityPosition[]>}
 */
export function groupPositionsByUserAndIp(positions) {
  return positions.reduce((groups, position) => {
    const key = `${position.user_id}-${position.ip_address}`
    if (!groups[key]) {
      groups[key] = []
    }
    groups[key].push(position)
    return groups
  }, {})
}
